import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { isFreeAdmin } from '../lib/freeAdmins';
import { api } from '../lib/apiClient';
import { PageWrapper } from '../components/layout/PageWrapper';
import { Seo } from '../components/Seo';
import { OrnamentDivider } from '../components/ui/OrnamentDivider';

interface DailyRow { date: string; views: number; uniqueSessions: number; uniqueUsers: number; }
interface PageRow { page: string; count: number; }
interface ReferrerRow { referrer: string; count: number; }
interface RecentRow {
  timestamp: string; page: string; referrer: string | null;
  userId: string | null; displayName: string | null;
  country: string | null; sessionId: string;
}
interface AnalyticsSummary {
  period: string;
  totals: { views: number; uniqueSessions: number; uniqueUsers: number };
  daily: DailyRow[];
  topPages: PageRow[];
  topReferrers: ReferrerRow[];
  recent: RecentRow[];
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="card-vintage p-4 flex flex-col gap-1">
      <span className="font-sans tabular-nums text-2xl font-bold text-mafia-gold">{value.toLocaleString('ru')}</span>
      <span className="text-[10px] font-mono text-mafia-muted uppercase tracking-widest">{label}</span>
    </div>
  );
}

function Bar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex-1 h-1.5 bg-mafia-border/30 rounded-full overflow-hidden">
      <div className="h-full bg-mafia-gold/60 rounded-full" style={{ width: `${pct}%` }} />
    </div>
  );
}

export function AdminAnalyticsPage() {
  const { user, loading } = useAuthStore();
  const isAdmin = !loading && !!user && isFreeAdmin(user.email);

  // Hooks must be called unconditionally — before any early returns
  const { data, isLoading, isError } = useQuery({
    queryKey: ['analytics-summary'],
    queryFn: () => api.get<AnalyticsSummary>('/analytics/summary'),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    enabled: isAdmin, // only fetch when confirmed admin
  });

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-6 h-6 border-2 border-mafia-gold/40 border-t-mafia-gold rounded-full animate-spin" />
    </div>
  );
  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <PageWrapper>
      <Seo title="Аналитика посещений" description="Статистика посещаемости сайта" path="/admin/analytics" />

      <div className="max-w-5xl mx-auto flex flex-col gap-8">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-mafia-muted mb-1">Администратор</p>
          <h1 className="font-display text-3xl font-bold text-mafia-gold">Аналитика</h1>
          <p className="text-mafia-paper-dim text-sm mt-1">{data?.period ?? 'последние 7 дней'}</p>
        </div>

        {isLoading && (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-mafia-gold/40 border-t-mafia-gold rounded-full animate-spin" />
          </div>
        )}

        {isError && <p className="text-mafia-crimson text-sm">Не удалось загрузить статистику</p>}

        {data && (
          <>
            {/* Totals */}
            <div className="grid grid-cols-3 gap-4">
              <StatCard label="Просмотры" value={data.totals.views} />
              <StatCard label="Сессии" value={data.totals.uniqueSessions} />
              <StatCard label="Пользователи" value={data.totals.uniqueUsers} />
            </div>

            <OrnamentDivider />

            {/* Daily chart */}
            <div className="flex flex-col gap-3">
              <h2 className="font-mono text-xs uppercase tracking-widest text-mafia-muted">По дням</h2>
              <div className="flex flex-col gap-2">
                {data.daily.map(row => {
                  const maxViews = Math.max(...data.daily.map(r => r.views), 1);
                  return (
                    <div key={row.date} className="flex items-center gap-3 text-xs font-sans tabular-nums">
                      <span className="text-mafia-muted w-24 shrink-0">{new Date(row.date).toLocaleDateString('ru', { day: '2-digit', month: 'short' })}</span>
                      <Bar value={row.views} max={maxViews} />
                      <span className="text-mafia-paper-dim w-8 text-right">{row.views}</span>
                      <span className="text-mafia-muted text-[10px] w-28 shrink-0">{row.uniqueSessions} сессий · {row.uniqueUsers} польз.</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <OrnamentDivider />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Top pages */}
              <div className="flex flex-col gap-3">
                <h2 className="font-mono text-xs uppercase tracking-widest text-mafia-muted">Топ страниц</h2>
                <div className="flex flex-col gap-1">
                  {data.topPages.map(row => {
                    const maxCount = data.topPages[0]?.count ?? 1;
                    return (
                      <div key={row.page} className="flex items-center gap-2 text-xs font-sans tabular-nums">
                        <span className="text-mafia-paper-dim truncate flex-1 min-w-0">{row.page}</span>
                        <Bar value={row.count} max={maxCount} />
                        <span className="text-mafia-muted w-8 text-right shrink-0">{row.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Top referrers */}
              <div className="flex flex-col gap-3">
                <h2 className="font-mono text-xs uppercase tracking-widest text-mafia-muted">Источники</h2>
                {data.topReferrers.length === 0 ? (
                  <p className="text-mafia-muted text-xs">Нет данных о переходах</p>
                ) : (
                  <div className="flex flex-col gap-1">
                    {data.topReferrers.map(row => {
                      const maxCount = data.topReferrers[0]?.count ?? 1;
                      return (
                        <div key={row.referrer} className="flex items-center gap-2 text-xs font-sans tabular-nums">
                          <span className="text-mafia-paper-dim truncate flex-1 min-w-0">{row.referrer}</span>
                          <Bar value={row.count} max={maxCount} />
                          <span className="text-mafia-muted w-8 text-right shrink-0">{row.count}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <OrnamentDivider />

            {/* Recent visits */}
            <div className="flex flex-col gap-3">
              <h2 className="font-mono text-xs uppercase tracking-widest text-mafia-muted">
                Последние визиты ({data.recent.length})
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px] font-sans tabular-nums border-collapse">
                  <thead>
                    <tr className="border-b border-mafia-border">
                      <th className="text-left px-2 py-2 text-mafia-muted font-mono font-normal tracking-wider">Время</th>
                      <th className="text-left px-2 py-2 text-mafia-muted font-mono font-normal tracking-wider">Страница</th>
                      <th className="text-left px-2 py-2 text-mafia-muted font-mono font-normal tracking-wider">Пользователь</th>
                      <th className="text-left px-2 py-2 text-mafia-muted font-mono font-normal tracking-wider">Источник</th>
                      <th className="text-left px-2 py-2 text-mafia-muted font-mono font-normal tracking-wider">Страна</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map((row, i) => (
                      <tr key={i} className={`border-b border-mafia-border/30 ${i % 2 === 0 ? '' : 'bg-mafia-card/30'}`}>
                        <td className="px-2 py-1.5 text-mafia-muted whitespace-nowrap">
                          {new Date(row.timestamp).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
                          {' '}
                          <span className="text-[10px]">{new Date(row.timestamp).toLocaleDateString('ru', { day: '2-digit', month: 'short' })}</span>
                        </td>
                        <td className="px-2 py-1.5 text-mafia-paper-dim max-w-[200px] truncate">{row.page}</td>
                        <td className="px-2 py-1.5">
                          {row.displayName ? (
                            <span className="text-mafia-gold">{row.displayName}</span>
                          ) : (
                            <span className="text-mafia-muted/50">гость</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-mafia-muted max-w-[150px] truncate">
                          {row.referrer ? (() => { try { return new URL(row.referrer).hostname; } catch { return row.referrer; } })() : '—'}
                        </td>
                        <td className="px-2 py-1.5 text-mafia-muted">{row.country ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </PageWrapper>
  );
}
